import { CustomerService, enumToExtendedArray, IApplicationFilter, PortfolioReportCache, property, widget, WidgetPropertyType } from '@dynamik/common';
import { InstrumentValuesReportViewModel } from '@dynamik/common/clients/reporting-clients';
import { BindingEngine, computedFrom } from 'aurelia-binding';
import { WidgetBase } from '@dynamik/common';
import { customElement } from 'aurelia-templating';
import { DisplayedFields } from '../displayed-fields';
import { CategoriesClient } from '@dynamik/common/clients/transaction-clients';
import { CategoryType } from '../../../classes/category-type';
import { IColumnSettings } from '../../dyn-table/dyn-table';
import { PortfolioOverviewGroupBy } from '../portfolio-overview-light-widget/portfolio-overview-light-widget';

@customElement('portfolio-overview-light-experimental-widget')
@widget('Portfolio overview (light) experimental', true, false, 'Description')
export class PortfolioOverviewLightExperimentalWidget extends WidgetBase {
  constructor(element: Element, customerService: CustomerService, private portfolioReportCache: PortfolioReportCache, bindingEngine: BindingEngine,
    private categoriesClient: CategoriesClient) {
    super(element, bindingEngine, customerService);
  }

  sourceData: InstrumentValuesReportViewModel[];
  allInstrumentValues: InstrumentValuesReportViewModel[];
  instrumentValues: InstrumentValuesReportViewModel[];
  total: InstrumentValuesReportViewModel;
  suppressSaveColumnState: boolean;
  PortfolioOverviewGroupBy = PortfolioOverviewGroupBy;
  currentPage = 0;
  lastItemRendered: (value: unknown) => void;
  DisplayedFields = DisplayedFields;

  @property('Data', undefined, WidgetPropertyType.List, enumToExtendedArray(PortfolioOverviewGroupBy))
  groupBy: PortfolioOverviewGroupBy = PortfolioOverviewGroupBy.None;

  @property('Data', 'Category type', WidgetPropertyType.List)
  categoryTypeId?: number;
  async categoryTypeIdListValues() {
    return this.customerService.instrumentCategories.filter(x => x.enabled).map(x => ({ value: x.catTypeId, label: x.catTypeName }));
  }

  @property('Data', 'Displayed fields', WidgetPropertyType.List)
  displayedFields?: DisplayedFields;
  async displayedFieldsListValues() {
    return [
      { value: undefined, label: 'Default' },
      { value: DisplayedFields.All, label: 'All fields' },
      { value: DisplayedFields.Fifo, label: 'FIFO' }
    ];
  }

  @computedFrom('displayedFields', 'customerService.selectedCustomer.useAverageCostPrice')
  get displayAvg() {
    return this.customerService.selectedCustomer.useAverageCostPrice && [undefined, DisplayedFields.All].includes(this.displayedFields);
  }

  @computedFrom('displayedFields', 'customerService.selectedCustomer.useAverageCostPrice')
  get displayFifo() {
    return !this.customerService.selectedCustomer.useAverageCostPrice || [DisplayedFields.Fifo, DisplayedFields.All].includes(this.displayedFields);
  }

  @property('Data', 'Portfolio', WidgetPropertyType.List)
  portfolio?: string;
  async portfolioListValues() {
    return [
      { value: undefined, label: 'All portfolios' },
      ...(await this.categoriesClient.getSecCategories(this.customerService.selectedCustomer.companyId, CategoryType.Portfolio))
        .map(x => ({ value: x.name, label: x.name }))
    ];
  }
  portfolioChanged(newValue: string, oldValue: string) {
    if (!this.title) {
      return;
    }

    this.title = this.title.replace(` (${oldValue})`, '') + (newValue ? ` (${newValue})` : '');
  }

  @property('Data')
  quantityPrecision?: number = 0;

  @property('Data')
  precision: number = 2;

  @property('Table')
  font?: string;

  @property('Table')
  fontSize?: number = 10;

  @property('Table')
  color?: string;

  @property('Table')
  rowHeight?: number;

  @property('Table')
  rowsPerPage: number = 30;

  /** Designed column configuration (order/visibility/widths/captions), persisted with the page. */
  @property(null)
  columnSettings?: IColumnSettings;

  /** Stable identity for per-user localStorage column state; generated when the widget's
   *  columns are designed and persisted with the page config. */
  @property(null)
  widgetInstanceId?: string;

  columnStorageKey?: string;

  @property()
  captionFont?: string;

  @property()
  captionFontSize?: number = 10;

  @property()
  captionColor?: string;

  /** End-user column adjustments, injected by ReportsBase (per-customer, DB-backed) on
   *  report surfaces. Deliberately NOT an @property: decorated fields are serialized into
   *  the page config on designer saves, which would bake one customer's user tier into
   *  the designed page. */
  userColumnSettings?: IColumnSettings;

  protected override async initialise(): Promise<void> {
    if (this.isDesigning) {
      if (!this.widgetInstanceId) {
        this.widgetInstanceId = crypto.randomUUID();
      }
    } else if (this.widgetInstanceId && !this.element.closest('.report-page')) {
      // Dashboards keep the localStorage tier; on report surfaces the user tier arrives
      // via userColumnSettings from ReportsBase (per customer, in the DB).
      this.columnStorageKey = `portfolio-overview-light-exp:${this.widgetInstanceId}`;
    }
  }

  /** Design surfaces: the report page editor, or a dashboard widget in edit mode. */
  private get isDesigning(): boolean {
    return !!this.element.closest('.report-page-editor, .grid-stack-item--edited');
  }

  /** User-tier diff from dyn-table (userSettings mode): re-dispatch enriched with the
   *  widget identity so the hosting report view can persist it per customer. */
  onColumnUserSettingsChanged(settings: IColumnSettings): void {
    if (this.isDesigning || !this.widgetInstanceId) {
      return;
    }
    this.element.dispatchEvent(new CustomEvent('widget-user-settings-changed', {
      bubbles: true,
      detail: { widgetInstanceId: this.widgetInstanceId, settings }
    }));
  }

  onColumnSettingsChanged(settings: IColumnSettings): void {
    if (!this.isDesigning) {
      return; // user adjustment: handled by the user tier (userColumnSettings/localStorage)
    }
    if (!this.widgetInstanceId) {
      this.widgetInstanceId = crypto.randomUUID();
    }
    this.columnSettings = settings;
    // Marks the page dirty so the designer's save persists the new configuration.
    this.element.dispatchEvent(new CustomEvent('widget-change', { bubbles: true }));
  }

  /** In the designer, reset clears the designed configuration back to the authored markup;
   *  for users the dyn-table itself already restored the designed defaults. */
  onColumnReset(): void {
    if (!this.isDesigning || !this.columnSettings) {
      return;
    }
    this.columnSettings = undefined;
    this.element.dispatchEvent(new CustomEvent('widget-change', { bubbles: true }));
  }

  protected override async loadData(filter: IApplicationFilter) {
    const values = await this.portfolioReportCache.instrumentValues(filter.customer.companyId, filter.dates.fromStr, filter.dates.toStr,
      [PortfolioOverviewGroupBy.Category, PortfolioOverviewGroupBy.TermAndCategory].includes(this.groupBy) ? this.categoryTypeId : undefined, this.portfolio, false);
    return values;
  }

  protected override async setData(data: InstrumentValuesReportViewModel[]) {
    var precisionLimit = Math.pow(0.1, this.precision);
    this.sourceData = data;
    data = data.filter(x => x.quantityClosingBalance > Math.pow(0.1, this.quantityPrecision)
      || Math.abs(x.estimatedValueClosingBalance) > precisionLimit
      || Math.abs(x.costPriceClosingBalance) > precisionLimit
      || Math.abs(x.unrealizedProfitLossChangeInPeriod) > precisionLimit
      || Math.abs(x.unrealizedProfitLossClosingBalance) > precisionLimit
      || Math.abs(x.realizedProfitLossChangeInPeriod) > precisionLimit
      || Math.abs(x.dividendChangeInPeriod) > precisionLimit
      || Math.abs(x.interestChangeInPeriod) > precisionLimit
      || Math.abs(x.profitLossChangeInPeriod) > precisionLimit
      || this.displayAvg && (
        Math.abs(x.avgCostPriceClosingBalance) > precisionLimit
        || Math.abs(x.avgUnrealizedProfitLossChangeInPeriod) > precisionLimit
        || Math.abs(x.avgUnrealizedProfitLossClosingBalance) > precisionLimit
        || Math.abs(x.avgRealizedProfitLossChangeInPeriod) > precisionLimit
        || Math.abs(x.avgProfitLossChangeInPeriod) > precisionLimit
      ));
    this.total = this.getTotal(data);
    switch (this.groupBy) {
      case PortfolioOverviewGroupBy.Type:
      case PortfolioOverviewGroupBy.Category: {
        const groupByField = this.groupBy === PortfolioOverviewGroupBy.Type ? 'type' : 'category';
        const grouped = Array.from(data.groupBy(x => x[groupByField])).sort((a, b) => a[0].localeCompare(b[0]));
        data = [];
        for (const [key, groupData] of grouped) {
          const groupHeader = new InstrumentValuesReportViewModel();
          groupHeader.group1Header = true;
          groupHeader.name = key;
          groupHeader.indent = 0;
          data.push(groupHeader);
          groupData.forEach(x => x.indent = 10);
          data.push(...groupData.sort((a, b) => a.name.localeCompare(b.name)));
          const groupTotal = this.getTotal(groupData);
          groupTotal.name = `Total ${key}`;
          groupTotal.group1Total = true;
          groupTotal.indent = 0;
          data.push(groupTotal);
        }
        break;
      }
      case PortfolioOverviewGroupBy.TermAndType:
      case PortfolioOverviewGroupBy.TermAndCategory:
        const termGroups = Array.from(data.groupBy(x => x.longTerm ? 'Long Term' : 'Short Term')).sort((a, b) => a[0].localeCompare(b[0]));
        data = [];
        for (const [term, termData] of termGroups) {
          const termHeader = new InstrumentValuesReportViewModel();
          termHeader.group1Header = true;
          termHeader.name = term;
          termHeader.indent = 0;
          data.push(termHeader);
          const groupByField = this.groupBy === PortfolioOverviewGroupBy.TermAndType ? 'type' : 'category';
          const groups = Array.from(termData.groupBy(x => x[groupByField])).sort((a, b) => a[0].localeCompare(b[0]));
          for (const [typeOrCategory, groupData] of groups) {
            const header = new InstrumentValuesReportViewModel();
            header.group2Header = true;
            header.name = typeOrCategory;
            header.indent = 10;
            data.push(header);
            groupData.forEach(x => x.indent = 20);
            data.push(...groupData.sort((a, b) => a.name.localeCompare(b.name)));
            const total = this.getTotal(groupData);
            total.name = `Total ${typeOrCategory}`;
            total.group2Total = true;
            total.indent = 10;
            data.push(total);
          }
          const termTotal = this.getTotal(termData);
          termTotal.name = `Total ${term}`;
          termTotal.group1Total = true;
          termTotal.indent = 0;
          data.push(termTotal);
        }
        break;
      default:
        data.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }

    this.allInstrumentValues = data;
    this.allInstrumentValues.push(this.total);

    // Populate excelData for right-click export
    this.excelData = {
      fileName: 'PortfolioOverviewLightExperimental',
      // data: { Items: data.filter(x => !x.group1Header || !x.group1Total) }
      data: { Items: this.sourceData }
      // data: {
      //   Items: data.filter(x => !x.groupHeader && !x.groupTotal).map(x => ({
      //     Name: x.name || '',
      //     Type: x.type || '',
      //     Category: x.category || '',
      //     'Isin/OrgNR': x.isin || x.orgNr || '',
      //     'Isin': x.isin || '',
      //     'OrgNr': x.orgNr || '',
      //     Quantity: x.quantityClosingBalance || 0,
      //     'Cost Price': x.costPriceClosingBalance || 0,
      //     'Estimated Value': x.estimatedValueClosingBalance || 0,
      //     'Unrealized P/L': x.unrealizedProfitLossChangeYearToDate || 0,
      //     'Realized P/L': x.realizedProfitLossChangeYearToDate || 0,
      //     Dividend: x.dividendChangeYearToDate || 0,
      //     Interest: x.interestChangeYearToDate || 0,
      //     'Total P/L': x.profitLossChangeYearToDate || 0
      //   }))
      // }
    };

    this.updatePageData();
  }

  private updatePageData() {
    this.instrumentValues = this.allInstrumentValues.filter((x, i) => this.currentPage * this.rowsPerPage <= i && i < (this.currentPage + 1) * this.rowsPerPage);
  }

  getTotal(x: InstrumentValuesReportViewModel[]) {
    return x.reduce((p, c) => {
      p.estimatedValueClosingBalance += c.estimatedValueClosingBalance;
      p.estimatedValuePercent += c.estimatedValuePercent;
      p.costPriceClosingBalance += c.costPriceClosingBalance;
      p.avgCostPriceClosingBalance += c.avgCostPriceClosingBalance;
      p.unrealizedProfitLossClosingBalance += c.unrealizedProfitLossClosingBalance;
      p.unrealizedProfitLossEndOfLastYear += c.unrealizedProfitLossEndOfLastYear;
      p.unrealizedProfitLossChangeInPeriod += c.unrealizedProfitLossChangeInPeriod;
      p.avgUnrealizedProfitLossClosingBalance += c.avgUnrealizedProfitLossClosingBalance;
      p.avgUnrealizedProfitLossEndOfLastYear += c.avgUnrealizedProfitLossEndOfLastYear;
      p.avgUnrealizedProfitLossChangeInPeriod += c.avgUnrealizedProfitLossChangeInPeriod;
      p.realizedProfitLossChangeInPeriod += c.realizedProfitLossChangeInPeriod;
      p.avgRealizedProfitLossChangeInPeriod += c.avgRealizedProfitLossChangeInPeriod;
      p.dividendChangeInPeriod += c.dividendChangeInPeriod;
      p.interestChangeInPeriod += c.interestChangeInPeriod;
      p.profitLossChangeInPeriod += c.profitLossChangeInPeriod;
      p.avgProfitLossChangeInPeriod += c.avgProfitLossChangeInPeriod;
      return p;
    },
      new InstrumentValuesReportViewModel({
        name: 'TOTAL', estimatedValueClosingBalance: 0, costPriceClosingBalance: 0, unrealizedProfitLossEndOfLastYear: 0, avgCostPriceClosingBalance: 0, avgUnrealizedProfitLossEndOfLastYear: 0,
        unrealizedProfitLossClosingBalance: 0, unrealizedProfitLossChangeInPeriod: 0, avgUnrealizedProfitLossClosingBalance: 0, avgUnrealizedProfitLossChangeInPeriod: 0, realizedProfitLossChangeInPeriod: 0,
        avgRealizedProfitLossChangeInPeriod: 0, dividendChangeInPeriod: 0, interestChangeInPeriod: 0, profitLossChangeInPeriod: 0, avgProfitLossChangeInPeriod: 0, estimatedValueChangeInPeriod: 0
      }));
  }

  getPageCount(): number {
    return this.instrumentValues ? Math.ceil(this.allInstrumentValues.length / this.rowsPerPage) : 0;
  }

  async setPage(page: number): Promise<void> {
    if (page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.updatePageData();
    await new Promise(resolve => this.lastItemRendered = resolve);
  }

  override configChanged(key: keyof PortfolioOverviewLightExperimentalWidget, newValue: any, oldValue: any) {
    switch (key) {
      case 'rowsPerPage':
        this.updatePageData();
        break
      case 'portfolio':
        this.portfolioChanged(newValue, oldValue);
      case 'quantityPrecision':
      case 'groupBy':
      case 'categoryTypeId':
      case 'precision':
      // displayedFields changes the column set; the reload recreates the table so
      // dyn-table re-resolves the layout against the new columns.
      case 'displayedFields':
        this.refresh$.next();
        break;
      default:
        break;
    }
  }
}
